/**
 * Initializes the page navigation functionality.
 *
 * This function sets up a sticky navigation bar that:
 * - Displays links to sections that have `showInPageNavigation` enabled
 * - Highlights the active link based on which section has crossed the active-section threshold
 * - Supports keyboard navigation (arrow keys to navigate, Enter to activate)
 * - Handles hash navigation (direct links to sections)
 *
 * The active section is determined by finding the section with the highest top position
 * that is still below the active-section threshold. When a section's top crosses that threshold,
 * it becomes the active section.
 */
function initializePageNavigation() {
    // Find the navigation container element
    const navigation = document.querySelector('.cms-page-navigation');
    const navigationFiller = document.querySelector('.cms-page-navigation-filler');
    if (!navigation) {
        return;
    }

    // Get the scrollable container, arrow containers, arrow buttons, and all navigation links
    const scrollContainer = navigation.querySelector('.cms-page-navigation-link-container');
    const leftArrowContainer = document.getElementById('left-arrow-container');
    const rightArrowContainer = document.getElementById('right-arrow-container');
    const leftArrowButton = leftArrowContainer ? leftArrowContainer.querySelector('button') : null;
    const rightArrowButton = rightArrowContainer ? rightArrowContainer.querySelector('button') : null;
    const links = Array.from(navigation.querySelectorAll('.cms-page-navigation-link'));
    const firstSectionAfterNav = document.querySelector('#cms-page-navigation ~ .cmsSection');

    if (links.length === 0) {
        return;
    }

    // Build section objects mapping links to their corresponding section elements
    const sectionsInNav = links
        .map((link) => {
            const sectionId = link.getAttribute('data-section-id');
            const sectionElement = document.getElementById(sectionId);
            return { link, sectionElement, sectionId };
        })
        .filter((item) => item.sectionElement !== null);

    if (sectionsInNav.length === 0) {
        return;
    }

    let activeSectionId = null;
    let focusedLinkIndex = -1;

    // Calculate offsets for active-section threshold calculation
    const mainNavigationHeight = document.getElementById('mainNavigation').getBoundingClientRect().height;
    const pageNavigationHeight = document.getElementById('cms-page-navigation').getBoundingClientRect().height;
    const totalOffset = mainNavigationHeight + pageNavigationHeight;

    const isMobile = document.getElementsByTagName('body')[0].classList.contains('mobile-page');

    /** Max distance (px) between current and target scroll; at or below this we treat programmatic scroll as a no-op. */
    const SCROLL_POSITION_EPSILON = 2;

    // When true, window scroll does not drive active-link updates (see handleScroll). Smooth scroll from a
    // nav click still emits many scroll events; without this, findActiveSection() would run each frame and
    // the highlight would step through every section along the path. Cleared on scrollend, or debounced scroll
    // settle when scrollend is unsupported (e.g. older Safari).
    let scrollEventListenerDisabled = false;

    /**
     * Returns the viewport Y coordinate used as the active-section threshold.
     *
     * This is main navigation height plus CMS page navigation height, plus a fixed 100px offset.
     *
     * @returns {number} Viewport Y position of the threshold line, in pixels from the top of the viewport.
     */
    function getActiveSectionThreshold() {
        return totalOffset + 100;
    }

    /**
     * Checks if the user has scrolled to the bottom of the page.
     *
     * Uses a 10px threshold to account for rounding differences and ensure
     * we catch cases where the user is effectively at the bottom.
     *
     * @returns {boolean} True if the user is at or near the bottom of the page
     */
    function isAtBottomOfPage() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        return scrollTop + windowHeight >= documentHeight - 10;
    }

    /**
     * Determines which section should be active based on scroll position.
     *
     * Finds the section that has most recently crossed the active-section threshold.
     *
     * @returns {string|null} The ID of the section that should be active
     */
    function findActiveSection() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const activeSectionThreshold = getActiveSectionThreshold();

        // Special case: If user is at the bottom, the last section should be active
        if (isAtBottomOfPage() && sectionsInNav.length > 0) {
            return sectionsInNav[sectionsInNav.length - 1].sectionId;
        }

        // Find the section with the highest top position that is still below the threshold
        let activeSection = null;
        let highestTop = -Infinity;

        for (let i = 0; i < sectionsInNav.length; i++) {
            const { sectionElement, sectionId } = sectionsInNav[i];
            const rect = sectionElement.getBoundingClientRect();

            if (rect.top < activeSectionThreshold && rect.top > highestTop) {
                highestTop = rect.top;
                activeSection = sectionId;
            }
        }

        // if we have scrolled at all on the page and don't have an active section,
        // and the first section after the nav is in the navigation, select the first section
        if (activeSection == null && scrollTop > 0 && sectionsInNav[0]?.sectionId === firstSectionAfterNav?.id) {
            activeSection = sectionsInNav[0]?.sectionId;
        }

        // Return found section, null otherwise
        return activeSection;
    }

    /**
     * Updates the visibility of scroll arrows based on scroll position.
     *
     * Called whenever the link container is scrolled.
     * - Left arrow: hidden when scrolled all the way to the left (scrollLeft === 0)
     * - Right arrow: hidden when scrolled all the way to the right
     * - Both arrows: hidden if container cannot scroll (content fits in viewport)
     */
    function updateArrowVisibility() {
        if (!scrollContainer || !leftArrowContainer || !rightArrowContainer) {
            return;
        }

        const scrollLeft = scrollContainer.scrollLeft;
        const scrollWidth = scrollContainer.scrollWidth;
        const clientWidth = scrollContainer.clientWidth;
        const canScroll = scrollWidth > clientWidth;
        const isAtStart = scrollLeft <= 1; // 1px threshold for rounding
        const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1; // 1px threshold for rounding

        // Hide both arrows if container cannot scroll
        if (!canScroll) {
            leftArrowContainer.style.display = 'none';
            rightArrowContainer.style.display = 'none';
            return;
        }

        // Hide left arrow when scrolled all the way to the left
        leftArrowContainer.style.display = isAtStart ? 'none' : '';

        // Hide right arrow when scrolled all the way to the right
        rightArrowContainer.style.display = isAtEnd ? 'none' : '';
    }

    /**
     * Scrolls a link into view within the horizontal scroll container.
     *
     * Checks if the link is outside the visible area and scrolls it to the
     * appropriate edge (left or right) with smooth animation.
     * Arrow visibility will be updated automatically via the scroll event listener.
     *
     * @param {HTMLElement} link - The link element to scroll into view
     */
    function scrollLinkIntoView(link) {
        if (!link || !scrollContainer) {
            return;
        }

        // on mobile, always scroll the link into the center of the nav
        if (isMobile) {
            link.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        } else {
            // on desktop only scroll if the link is not fully visible
            const linkRect = link.getBoundingClientRect();
            const containerRect = scrollContainer.getBoundingClientRect();

            if (linkRect.left < containerRect.left + 100) {
                link.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
            } else if (linkRect.right > containerRect.right - 100) {
                link.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'end' });
            }
        }
    }

    /**
     * Scrolls a section so its top aligns with the active-section threshold.
     *
     * Uses the same viewport Y as getActiveSectionThreshold() (nav stack height + 100px).
     * Target Y is clamped to the document maximum so the last section matches the real scroll end
     * (avoids no movement + stuck scrollEventListenerDisabled when already at bottom).
     * If already within SCROLL_POSITION_EPSILON px of the clamped target, does not scroll.
     * Otherwise sets scrollEventListenerDisabled before smooth scroll (scrollend / debounce clears it).
     *
     * @param {HTMLElement} sectionElement - The section element to scroll to
     */
    function scrollToSection(sectionElement) {
        const sectionRect = sectionElement.getBoundingClientRect();
        const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const activeSectionThreshold = getActiveSectionThreshold();
        const sectionTopAbsolute = currentScrollTop + sectionRect.top;
        const targetScrollTop = Math.max(0, sectionTopAbsolute - activeSectionThreshold + 1);
        const maxScrollTop = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        const clampedScrollTop = Math.min(targetScrollTop, maxScrollTop);

        if (Math.abs(clampedScrollTop - currentScrollTop) <= SCROLL_POSITION_EPSILON) {
            return;
        }

        scrollEventListenerDisabled = true;
        window.scrollTo({
            top: clampedScrollTop,
            behavior: 'smooth',
        });
    }

    /**
     * Updates tabindex attributes so only the active link is tabbable.
     * @param {string} activeSectionId - The ID of the section that should be tabbable
     */
    function updateTabIndexes(activeSectionId) {
        sectionsInNav.forEach(({ link, sectionId }) => {
            link.setAttribute('tabindex', sectionId === activeSectionId ? '0' : '-1');
        });
    }

    /**
     * Activates a section (scrolls to it, updates active state, moves focus).
     *
     * This is the shared logic for activating a section whether triggered by
     * click, Enter key, or hash navigation.
     *
     * @param {string} sectionId - The ID of the section to activate
     */
    function activateSection(sectionId) {
        const section = sectionsInNav.find(({ sectionId: id }) => id === sectionId);
        if (!section) {
            return;
        }

        // Update URL hash
        history.pushState(null, null, '#' + sectionId);

        // Scroll to section at active-section threshold (sets scrollEventListenerDisabled only if scroll runs)
        scrollToSection(section.sectionElement);

        // Update active link state
        updateActiveLink(sectionId);

        // Move focus to section element
        // prevent scroll, scrollToSectionAtActiveThreshold handles this
        section.sectionElement.focus({ preventScroll: true });
    }

    /**
     * Updates the active state of navigation links.
     *
     * Updates CSS classes, tabindex attributes, and scrolls active link into view.
     *
     * @param {string} sectionId - The ID of the section that should be active
     */
    function updateActiveLink(sectionId) {
        if (activeSectionId === sectionId) {
            return;
        }

        // Update active class on all links
        sectionsInNav.forEach(({ link, sectionId: id }) => {
            link.classList.toggle('active', id === sectionId);
        });

        // Update state
        activeSectionId = sectionId;
        const newFocusedIndex = sectionsInNav.findIndex(({ sectionId: id }) => id === sectionId);
        if (newFocusedIndex !== -1) {
            focusedLinkIndex = newFocusedIndex;
        }

        // Update tabindex and scroll active link into view
        updateTabIndexes(activeSectionId);
        const activeLink = sectionsInNav.find(({ sectionId: id }) => id === sectionId);
        if (activeLink) {
            scrollLinkIntoView(activeLink.link);
        }
    }

    // ============================================================================
    // EVENT HANDLERS
    // ============================================================================

    /**
     * Handles scroll events to update the active section.
     */
    function handleScroll() {
        if (scrollEventListenerDisabled) {
            // Programmatic smooth scroll is in flight: skip findActiveSection / updateActiveLink and sticky-nav
            // logic that would otherwise react to every intermediate scroll position.
            return;
        }
        const newActiveSection = findActiveSection();
        updateActiveLink(newActiveSection);

        if (!navigation.classList.contains('fixed') && navigation.getBoundingClientRect().top <= mainNavigationHeight) {
            navigation.classList.add('fixed');
        } else if (
            navigation.classList.contains('fixed') &&
            navigationFiller.getBoundingClientRect().top > mainNavigationHeight
        ) {
            navigation.classList.remove('fixed');
        }
    }

    /**
     * Handles click events on navigation links.
     */
    function handleLinkClick(event) {
        event.preventDefault();
        const sectionId = event.currentTarget.getAttribute('data-section-id');
        activateSection(sectionId);
    }

    /**
     * Handles keyboard navigation within the navigation bar.
     *
     * Arrow keys navigate between links (changing focus but not activating).
     * Enter key activates the focused link.
     */
    function handleKeyboardNavigation(event) {
        // Only handle when focus is within navigation container
        if (!scrollContainer.contains(document.activeElement)) {
            return;
        }

        const activeLink = document.activeElement;
        if (!activeLink.classList.contains('cms-page-navigation-link')) {
            return;
        }

        const currentIndex = sectionsInNav.findIndex(({ link }) => link === activeLink);
        if (currentIndex === -1) {
            return;
        }

        // Arrow keys: navigate between links
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (currentIndex > 0) {
                const previousLink = sectionsInNav[currentIndex - 1].link;
                // prevent scroll, scrollLinkIntoView handles this
                previousLink.focus({ preventScroll: true });
                focusedLinkIndex = currentIndex - 1;
                scrollLinkIntoView(previousLink);
            }
        } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            event.preventDefault();
            if (currentIndex < sectionsInNav.length - 1) {
                const nextLink = sectionsInNav[currentIndex + 1].link;
                // prevent scroll, scrollLinkIntoView handles this
                nextLink.focus({ preventScroll: true });
                focusedLinkIndex = currentIndex + 1;
                scrollLinkIntoView(nextLink);
            }
        }
        // Enter key: activate focused link
        else if (event.key === 'Enter') {
            event.preventDefault();
            const focusedSection = sectionsInNav[currentIndex];
            if (focusedSection) {
                activateSection(focusedSection.sectionId);
            }
        }
    }

    /**
     * Handles focus events on the navigation container.
     *
     * When the user tabs into the navigation, focus should go to the active link.
     * This ensures keyboard users always start with the active link focused.
     *
     * @param {FocusEvent} event - The focus event
     */
    function handleNavigationFocus(event) {
        if (event.target === navigation || event.target === scrollContainer) {
            const activeLink = sectionsInNav.find(({ sectionId }) => sectionId === activeSectionId);
            if (activeLink) {
                event.preventDefault();
                scrollLinkIntoView(activeLink.link);
                // prevent scroll, scrollLinkIntoView handles this
                activeLink.link.focus({ preventScroll: true });
                const activeIndex = sectionsInNav.findIndex(({ sectionId }) => sectionId === activeSectionId);
                if (activeIndex !== -1) {
                    focusedLinkIndex = activeIndex;
                }
            }
        }
    }

    /**
     * Handles click events on arrow buttons to scroll the navigation container.
     *
     * Scrolls the container by 200px in the respective direction.
     * Arrow visibility will be updated automatically via the scroll event listener.
     *
     * @param {MouseEvent} event - The click event
     */
    function handleArrowClick(event) {
        if (!scrollContainer) {
            return;
        }

        event.preventDefault();
        const currentScrollLeft = scrollContainer.scrollLeft;
        const scrollAmount = 200;

        if (event.currentTarget === leftArrowButton) {
            // Scroll left by 200px
            scrollContainer.scrollTo({
                left: Math.max(0, currentScrollLeft - scrollAmount),
                behavior: 'smooth',
            });
        } else if (event.currentTarget === rightArrowButton) {
            // Scroll right by 200px
            scrollContainer.scrollTo({
                left: currentScrollLeft + scrollAmount,
                behavior: 'smooth',
            });
        }
    }

    // Set up event listeners
    sectionsInNav.forEach(({ link }) => {
        link.addEventListener('click', handleLinkClick);
    });

    navigation.addEventListener('focusin', handleNavigationFocus);
    navigation.addEventListener('keydown', handleKeyboardNavigation);

    // Set up arrow button click handlers
    if (leftArrowButton) {
        leftArrowButton.addEventListener('click', handleArrowClick);
    }
    if (rightArrowButton) {
        rightArrowButton.addEventListener('click', handleArrowClick);
    }

    // Make section elements focusable
    sectionsInNav.forEach(({ sectionElement }) => {
        if (sectionElement && !sectionElement.hasAttribute('tabindex')) {
            sectionElement.setAttribute('tabindex', '-1');
        }
    });

    // Set up scroll listener on link container to update arrow visibility
    scrollContainer.addEventListener('scroll', updateArrowVisibility, { passive: true });

    // Update arrow visibility on window resize (container size may change)
    window.addEventListener('resize', updateArrowVisibility, { passive: true });

    const supportsScrollend = 'onscrollend' in window;
    /** Debounce when scrollend is missing: time with no scroll events before re-enabling scroll-driven updates. */
    const SCROLL_SETTLE_MS = 100;

    // Set up scroll listener with requestAnimationFrame throttling
    let scrollTimeout;
    let scrollSettleTimeoutId;
    window.addEventListener(
        'scroll',
        function () {
            if (!supportsScrollend && scrollEventListenerDisabled) {
                clearTimeout(scrollSettleTimeoutId);
                scrollSettleTimeoutId = setTimeout(function () {
                    scrollSettleTimeoutId = undefined;
                    if (scrollEventListenerDisabled) {
                        scrollEventListenerDisabled = false;
                    }
                }, SCROLL_SETTLE_MS);
            }
            if (scrollTimeout) {
                cancelAnimationFrame(scrollTimeout);
            }
            scrollTimeout = requestAnimationFrame(handleScroll);
        },
        { passive: true },
    );

    window.addEventListener(
        'scrollend',
        function () {
            // Smooth scroll (including scroll-to-section from the nav) has finished. Turn scroll-driven
            // highlighting back on so wheel/touch/trackpad scrolling keeps the active tab updated.
            if (scrollEventListenerDisabled) {
                scrollEventListenerDisabled = false;
            }
        },
        { passive: true },
    );

    // Initialize active section
    handleScroll();

    // Initialize tabindex and focused index
    if (activeSectionId) {
        updateTabIndexes(activeSectionId);
        const initialIndex = sectionsInNav.findIndex(({ sectionId }) => sectionId === activeSectionId);
        if (initialIndex !== -1) {
            focusedLinkIndex = initialIndex;
        }
    }

    // Initialize arrow visibility
    updateArrowVisibility();

    // Handle hash navigation
    if (window.location.hash) {
        const hashSectionId = window.location.hash.substring(1);
        const hashSection = sectionsInNav.find(({ sectionId }) => sectionId === hashSectionId);
        if (hashSection) {
            setTimeout(() => {
                activateSection(hashSectionId);
            }, 100);
        }
    }
}
